import * as RadixTooltip from '@radix-ui/react-tooltip'
import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Material-Design tooltip wrapper around Radix UI Tooltip.
 *
 * Pass `<TooltipProvider delayDuration={150}>` once at the app root (already
 * done in main.tsx). Then use `<Tooltip content="...">` to wrap any element
 * that should show a tooltip on hover/focus.
 *
 * Defaults:
 *  - 150 ms show delay (override on per-tooltip basis if needed)
 *  - fade + zoom animation, theme-aware popover background
 *  - arrow pointing at the trigger
 *  - dismisses on cursor leave / blur / escape
 *
 * The trigger element receives the tooltip via Radix's `asChild` pattern, so
 * the trigger keeps its own DOM tag and event handlers intact.
 */

export const TooltipProvider = RadixTooltip.Provider

interface TooltipProps {
  /** The element the tooltip describes. Will be rendered as the trigger via Radix asChild. */
  children: ReactNode
  /** Tooltip body. Plain string or any ReactNode (icons, multi-line, bold, etc.). */
  content: ReactNode
  /** Side the tooltip prefers (Radix auto-flips on viewport edge). Default 'top'. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Tooltip alignment relative to the trigger. Default 'center'. */
  align?: 'start' | 'center' | 'end'
  /** Override the global show delay for this tooltip. */
  delayDuration?: number
  /** Disable the tooltip (e.g., when content is empty). Default false. */
  disabled?: boolean
  /** Extra Tailwind classes for the content panel. */
  className?: string
}

export function Tooltip({
  children,
  content,
  side = 'top',
  align = 'center',
  delayDuration,
  disabled = false,
  className,
}: TooltipProps) {
  if (disabled || content === null || content === undefined || content === '') {
    return <>{children}</>
  }
  return (
    <RadixTooltip.Root delayDuration={delayDuration}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          className={cn(
            'z-50 max-w-[280px] rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
            className,
          )}
        >
          {content}
          <RadixTooltip.Arrow className="fill-popover" width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  )
}
